<template>
  <nav class="box-border h-[60px] pl-[24px] pr-[32px] flex items-center bg-[var(--color-primary)] text-white">
    <!-- logo & title -->
    <div
      v-if="settings.showNavLogo"
      class="flex justify-center items-center cursor-pointer"
      @click="router.push('/')"
    >
      <img
        class="w-[117px] h-[34px] ml-[9px] mr-[49px]"
        :src="store?.oemBrandDTO?.menuLogo || defaultNavLogo"
        alt="menu-log"
      >
    </div>

    <!-- <div v-if="!showTopMenus" class="nav-left flex items-center">
      <el-breadcrumb>
        <el-breadcrumb-item
          v-for="(v, idx) in menusStore.breadcrumbs"
          :key="idx"
          class="breadcrumb-label"
          :class="[{ active: idx === menusStore.breadcrumbs.length - 1 }]"
        >
          {{ v.meta?.title }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div> -->

    <div v-if="showTopMenus && !noTopMenus" class="flex h-full">
      <div
        v-for="(v, idx) in menusStore.topMenus"
        :key="idx"
        class="top-menu"
        :class="{ active: v.name === activeMenu.name || !activeMenu.name && v.name === indexPath.slice(1) }"
        @click="changeSubMenus(v.routeName)"
      >
        <!-- <svg-icon :icon="v.icon" /> -->
        <span>{{ v.label }}</span>
      </div>
    </div>

    <div class="nav-right absolute right-[41px] flex items-center gap-x-4">
      <el-dropdown
        placement="bottom"
        trigger="click"
        class="mr-2"
      >
        <div
          class="user-switch-box"
        >
          <span
            :class="{
              'saas-ico': currentIdentity.systemType === ESystemType.SAAS,
              'agent-ico': currentIdentity.systemType === ESystemType.Agent,
              'company-ico': currentIdentity.systemType === ESystemType.Company,
              'server-ico': currentIdentity.systemType === ESystemType.Server,
            }"
          >{{ getIdentityName(currentIdentity) }}</span>
          <div class="switch-arrow">
            <svg-icon icon="icon-park-solid:down-one" />
          </div>
        </div>
        <template #dropdown>
          <el-dropdown-menu class="nav-down-item">
            <el-dropdown-item
              v-for="v in identityList"
              :key="v.identityId"
              :class="{
                active: v.identityId === currentIdentity.identityId,
                'saas-ico': v.systemType === ESystemType.SAAS,
                'agent-ico': v.systemType === ESystemType.Agent,
                'company-ico': v.systemType === ESystemType.Company,
                'server-ico': v.systemType === ESystemType.Server,
              }"
              class="nav-dropdown"
              @click="switchIdentity(v)"
            >
              {{ getIdentityName(v) }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <svg-icon v-if="!isServer" icon="tabler:arrow-bar-to-down" class="!w-6 !h-6" @click="router.push('/user-center/download')" />
      
      <div class="avatar">
        <el-dropdown :teleported="false" placement="bottom-end" trigger="click">
          <div class="user-nickname-box">
            <img
              :src="DefaultHead"
              alt="user"
            >
          </div>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item @click="router.push('/user-center/info')">
                个人中心
              </el-dropdown-item>
              <el-dropdown-item @click="doLogout">
                退出账号
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
  </nav>

  <table-columns-setting />

  <dragger-robot
    v-if="isCompany && modules.length > 0"
    :modules="modules"
    @click="onModuleClick"
  />

  <guide-entry
    :entries="guideEntires"
    @click="onGuideClick"
    @close="guideEntires = []"
  />
</template>

<script setup lang="ts">
import { getWithTopMenus, indexPath, queryBusinessLineList, useMenusStore, useTabsStore, useUserStore } from '../..';
import { onConfirm, SvgIcon, DraggerRobot, GuideEntry, IGuideEntry, crowdEntries, matchEntries, IGuideEntryButton } from '@vue3/components';
import { ref, computed, nextTick, onBeforeMount, onMounted, toRaw, watch } from 'vue';
import DefaultHead from '@app/assets/images/default-user.png';
import { useRoute, useRouter } from 'vue-router';
import { type IIdentity, setIdentityKey, getIdentityName, ESystemType, settings, getSysTypeValue, getSystemTypeFromUrl, updateSysType, checkSwitchIdentityError } from '@core/api';
import { isNull } from '@core/tools';
import { useLocalStorage } from '@vueuse/core';
import defaultNavLogo from '@core/main/assets/images/default-nav-logo.png';
import TableColumnsSetting from '../components/table-columns-settings/TableColumnsSetting.vue';

defineEmits(['toggle-sidebar']);

defineProps({
  noTopMenus: {
    type: Boolean,
    default: false,
  },
});

const modules = computed(() => [
  bussinessIds.value.includes(3)
    ? {
      id: 'crowd',
      name: '众包灵工',
      icon: 'material-symbols:nest-secure-alarm-outline-sharp',
    }
    : null,
  bussinessIds.value.includes(4)
    ? {
      id: 'match',
      name: '撮合灵工',
      icon: 'mingcute:link-3-line',
    }
    : null,
  // 暂未开放
  // bussinessIds.value.includes(2)
  //   ? {
  //     id: 'split',
  //     name: '智慧分账',
  //     icon: 'heroicons:currency-dollar',
  //     disabled: true,
  //   }
  //   : null,
].filter(Boolean));

const guideEntires = ref<IGuideEntry[]>([]);

const BroadcastChannelName = 'BroadcastChannelName';
const SwitchIdenEventName = 'switch';
const LogoutEventName = 'logout';

const router = useRouter();
const route = useRoute();

const store = useLocalStorage(
  settings.oemkeys,
  {} as any,
);

const { logout, afterLogout, state: userState } = useUserStore();
const { state: menusStore, currRoute, setSideMenus, getActiveTopMenu } = useMenusStore();
const { removeAll } = useTabsStore();

const routes = computed(() => router.getRoutes());
const bussinessIds = ref<number[]>([]);
const identityList = computed(() => userState.identities || []);
const currentIdentity = computed(() => userState.identity);
const currentSystemType = computed(() => currentIdentity.value?.systemType);
const showTopMenus = computed(() => getWithTopMenus(currentSystemType.value));
const isCompany = computed(() => currentIdentity.value?.systemType === ESystemType.Company);
const isServer = computed(() => currentIdentity.value?.systemType === ESystemType.Server);

const activeMenu = computed(() => getActiveTopMenu());

const onModuleClick = (id: string) => {
  guideEntires.value = {
    crowd: crowdEntries,
    match: matchEntries,
    split: [],
  }[id];
};

const onGuideClick = ({ btn }: { btn: IGuideEntryButton }) => {
  guideEntires.value = [];

  router.push({
    name: btn.id,
    query: {
      guide: '1',
      mode: btn.mode || undefined,
    },
  });
};

const switchIdentity = async (item: IIdentity) => {
  if (item.identityId === currentIdentity.value?.identityId) return;

  const error = await checkSwitchIdentityError(item.identityId);

  if (error) {
    onConfirm({
      title: '温馨提示',
      message: error,
      type: 'error',
      okText: '确定',
      showCancelButton: false,
      closeOnClickModal: false,
      showClose: false,
    });
    return;
  }

  updateSysType(String(item.systemType));
  setIdentityKey(String(item.identityId));
  sessionStorage.clear();
  nextTick(() => {
    setTimeout(() => {
      const bc = new BroadcastChannel(BroadcastChannelName);

      bc.postMessage(SwitchIdenEventName);
      toIndexPath(item.systemType);
    }, 200);
  });
};

const toIndexPath = (systemType?: ESystemType) => {
  systemType = isNull(systemType) ? Number(getSysTypeValue()) : systemType;

  const ports = {
    [ESystemType.SAAS]: __SAAS_APP_PORT__,
    [ESystemType.Agent]: __AGENT_APP_PORT__,
    [ESystemType.Company]: __COMPANY_APP_PORT__,
    [ESystemType.Server]: __SERVER_APP_PORT__,
  };

  const prefixs = {
    [ESystemType.SAAS]: 'identity-saas',
    [ESystemType.Agent]: 'identity-agent',
    [ESystemType.Company]: 'identity-company',
    [ESystemType.Server]: 'identity-server',
  };

  removeAll();

  if (__ENV_DEV__) {
    window.location.href = `${location.protocol}//${location.hostname}:${ports[systemType]}/${prefixs[systemType!]}`;
  } else {
    console.log('to Login Page from toIndexPath');
    window.location.href = `/${prefixs[systemType!]}`;
  }
};

const changeSubMenus = (name: string) => {
  setSideMenus(name);

  router.push({ name });
};

const doLogout = () => {
  onConfirm({
    message: '确定退出系统吗？',
    type: 'warning',
    onOk: async () => {
      await logout();

      const bc = new BroadcastChannel(BroadcastChannelName);

      bc.postMessage(LogoutEventName);
    },
  });
};

async function fetchPreRechargeData() {
  try {
    const items = await queryBusinessLineList();

    bussinessIds.value = items?.map((v) => v.businessId) || [];
  } catch (error) {
    console.error(error);
  }
}

onBeforeMount(() => {
  const bc = new BroadcastChannel(BroadcastChannelName);

  bc.onmessage = (e) => {
    if (e.data === SwitchIdenEventName) toIndexPath();
    if (e.data === LogoutEventName) afterLogout();
  };

  menusStore.registedRoutes = routes.value;
  window.Navgation = router;
  window.CurrentRoute = route;
});

onMounted(() => {
  const systemTypeFromUrl = getSystemTypeFromUrl();

  if (!systemTypeFromUrl) return;

  const systemType = Number(getSysTypeValue());

  if (systemTypeFromUrl !== systemType) toIndexPath(systemType);
});

watch(
  () => route.name,
  () => {
    currRoute.value = {
      ...toRaw(route),
      matched: [],
    };
  },
  { immediate: true },
);

fetchPreRechargeData();
</script>

<style lang="scss" scoped>
nav {
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);

  background-image: url('../../assets/images/nav-bg.png');
  background-size: 100% 100%;
  background-position: 0 0;
}

.top-menu {
  @apply flex items-center h-full px-[16px] text-[16px] cursor-pointer;

  span {
    @apply pl-1
  }

  &.active {
    @apply bg-[var(--top-menu-active-bg)];
  }
}

.nav-right {
  .svg-icon {
    @apply cursor-pointer hover:opacity-70;
  }
}

.user-nickname-box {
  @apply cursor-pointer flex items-center text-[#fff] text-sm select-none;

  &:hover {
    @apply opacity-75;
  }

  img {
    @apply w-[44px] h-[44px];
  }
}

.nav-down-item {
  max-height: 550px;
  overflow-y: auto;

  :deep(.el-dropdown-menu__item) {
    @apply block whitespace-nowrap text-ellipsis overflow-hidden w-[290px];
  }
}

.avatar {

  :deep(.el-dropdown-menu__item) {
    @apply w-[80px] text-[#00000095] text-[14px] flex items-center justify-center px-3 py-[5px] !important;
  }
}

.user-logout-btn {
  @apply relative top-[2px];

  * {
    color: #fff;
  }
}

.user-switch-box {
  @apply cursor-pointer bg-[#ffffff20] text-[var(--color-text-white)] text-sm w-[290px] h-[32px] select-none box-border rounded-sm px-[16px] flex items-center justify-between;

  .switch-arrow {
    @apply w-[16px] h-[16px] rounded-sm flex items-center justify-center text-[var(--color-white)];
  }

  & > span {
    @apply whitespace-nowrap text-ellipsis w-full overflow-hidden bg-no-repeat pl-6;

    background-size: 18px;
    background-position: 0 1px;

    &.saas-ico {
      background-image: url(@core/main/assets/images/saas-ico.png);
    }

    &.company-ico {
      background-image: url(@core/main/assets/images/company-ico.png);
    }

    &.agent-ico {
      background-image: url(@core/main/assets/images/agent-ico.png);
    }

    &.server-ico {
      background-image: url(@core/main/assets/images/server-ico.png);
    }
  }

  &:hover {
    & > span, & > .switch-arrow {
      @apply opacity-75;
    }
  }
}

.user-dropdown-items {
  @apply absolute top-[44px] right-[246px] w-[196px] bg-white box-border py-[8px] rounded-sm z-50;

  border: 1px #ddd solid;
}

:deep(.el-breadcrumb__item) {
  span {
    color: var(--breadcrumb-color) !important;
    font-size: 12px;
  }

  &.active {
    span {
      color: var(--color-text-white) !important;
    }
  }
}

:deep(.el-dropdown-menu__item) {
  background-repeat: no-repeat;
  background-size: 18px;
  background-position: 12px 7px;
  padding-left: 39px;

  &:focus {
    background-color: transparent !important;
    color: var(--color-text-primary) !important;
  }

  &:hover {
    background-color: var(--color-gray-hover) !important;
    color: var(--color-primary) !important;
  }

  &.active {
    background-color: var(--color-emphasis);
    color: white;

    &:focus,
    &:hover {
      background-color: var(--color-emphasis) !important;
      color: white !important;
    }
  }

  &.saas-ico {
    background-image: url(@core/main/assets/images/saas-ico.png);
  }

  &.company-ico {
    background-image: url(@core/main/assets/images/company-ico.png);
  }

  &.agent-ico {
    background-image: url(@core/main/assets/images/agent-ico.png);
  }

  &.server-ico {
    background-image: url(@core/main/assets/images/server-ico.png);
  }
}
</style>
